import React, { useState, useEffect } from 'react';
import Button from '../../ui/Button';
import Card from '../../ui/Card';
import { StatusBadge } from '../../ui/Table';
import { formatBytes, formatDuration, formatTime, getSourceName } from './utils';
import { STATUS_VARIANTS } from './constants';
import axiosInstance from '../../api/axiosInstance';
import logger from '../../../utils/logger';

export default function JobDetailPanel({ jobId, onClose, onRetry, showNotification }) {
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let interval = null;

    const fetchJob = async () => {
      try {
        const response = await axiosInstance.get('/api/business/email-migration/jobs');
        if (response.data.success && !cancelled) {
          const found = response.data.jobs.find(j => j.jobId === jobId || j._id === jobId);
          setJob(found || null);
          // Stop polling once the job reaches a terminal state
          if (found && found.status !== 'running' && interval) {
            clearInterval(interval);
            interval = null;
          }
        }
      } catch (err) {
        logger.error('Failed to fetch job:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchJob();
    interval = setInterval(fetchJob, 5000);
    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
    };
  }, [jobId]);

  // Estimated remaining time for a running job, from elapsed time vs progress
  const remainingSeconds = (() => {
    if (!job || job.status !== 'running' || !job.startedAt || !job.progress || job.progress <= 0) return null;
    const elapsedMs = Date.now() - new Date(job.startedAt).getTime();
    if (elapsedMs <= 0) return null;
    return Math.round((elapsedMs * (100 - job.progress)) / job.progress / 1000);
  })();

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axiosInstance.delete(`/api/business/email-migration/jobs/${jobId}`);
      showNotification?.('Job deleted', 'success');
      onClose();
    } catch (err) {
      showNotification?.(err.response?.data?.message || 'Failed to delete', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleRetry = async () => {
    try {
      await axiosInstance.post(`/api/business/email-migration/jobs/${jobId}/retry`);
      showNotification?.('Migration restarted', 'success');
      onRetry(jobId);
    } catch (err) {
      showNotification?.(err.response?.data?.message || 'Failed to retry', 'error');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <svg className="w-6 h-6 text-orange-500 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth={4} className="opacity-25" />
          <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" className="opacity-75" />
        </svg>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Job not found</p>
        <Button variant="ghost" onClick={onClose} className="mt-3">Close</Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-1">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            {getSourceName(job.sourceType)}
          </h3>
          <StatusBadge variant={STATUS_VARIANTS[job.status] || 'gray'}>
            {job.status}
          </StatusBadge>
        </div>
      </div>

      {/* Progress */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-gray-500">Progress</span>
          <span className="font-medium text-gray-900 dark:text-white">{job.progress || 0}%</span>
        </div>
        <div className="h-2.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full ${
              job.status === 'completed' ? 'bg-emerald-500' :
              job.status === 'failed' ? 'bg-red-500' :
              job.status === 'cancelled' ? 'bg-amber-500' : 'bg-orange-500'
            }`}
            style={{ width: `${job.progress || 0}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-xs text-gray-400">
          <span>{job.completedUsers || 0}/{job.totalUsers || 0} users</span>
          <span>
            {remainingSeconds != null && (
              <span className="mr-2 text-orange-500">~{formatTime(remainingSeconds)} remaining</span>
            )}
            {formatBytes(job.transferredData)}
          </span>
        </div>
      </div>

      {/* Info Grid */}
      <Card padding="md">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="text-xs text-gray-500 block">Started</span>
            <span className="text-sm text-gray-900 dark:text-white">
              {job.startedAt ? new Date(job.startedAt).toLocaleString() : '--'}
            </span>
          </div>
          <div>
            <span className="text-xs text-gray-500 block">Completed</span>
            <span className="text-sm text-gray-900 dark:text-white">
              {job.completedAt ? new Date(job.completedAt).toLocaleString() : '--'}
            </span>
          </div>
          <div>
            <span className="text-xs text-gray-500 block">Duration</span>
            <span className="text-sm text-gray-900 dark:text-white">
              {formatDuration(job.startedAt, job.completedAt)}
            </span>
          </div>
          <div>
            <span className="text-xs text-gray-500 block">Source Server</span>
            <span className="text-sm font-mono text-gray-900 dark:text-white">
              {job.sourceDetails?.hostname || '--'}
            </span>
          </div>
        </div>
      </Card>

      {/* Domain Mappings */}
      {job.domainMapping && Object.keys(job.domainMapping).length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Domain Mappings</h4>
          <div className="space-y-1.5">
            {Object.entries(job.domainMapping).map(([from, to]) => (
              <div key={from} className="flex items-center gap-2 px-3 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg text-sm">
                <span className="text-gray-700 dark:text-gray-300">{from}</span>
                <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
                <span className="font-medium text-gray-900 dark:text-white">{typeof to === 'object' ? to.name : to}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* User Migrations */}
      {job.userMigrations && job.userMigrations.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">User Migrations</h4>
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg max-h-48 overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 dark:bg-gray-800 sticky top-0">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">Email</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">Status</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">Data</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {job.userMigrations.map((um, idx) => (
                  <tr key={idx}>
                    <td className="px-3 py-2 font-mono text-gray-900 dark:text-white">{um.sourceEmail || um.username}</td>
                    <td className="px-3 py-2">
                      <StatusBadge variant={STATUS_VARIANTS[um.status] || 'gray'} className="text-xs">
                        {um.status}
                      </StatusBadge>
                    </td>
                    <td className="px-3 py-2 text-gray-500">{formatBytes(um.transferredData)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Errors */}
      {job.errors && job.errors.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-red-600 dark:text-red-400 mb-2">Errors ({job.errors.length})</h4>
          <div className="space-y-1.5 max-h-32 overflow-y-auto">
            {job.errors.map((err, idx) => (
              <div key={idx} className="px-3 py-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-xs text-red-700 dark:text-red-300">
                {err}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
        {(job.status === 'failed' || job.status === 'cancelled') && (
          <Button variant="primary" size="sm" onClick={handleRetry}>
            Retry Migration
          </Button>
        )}
        {job.status !== 'running' && (
          <Button variant="danger" size="sm" onClick={handleDelete} loading={deleting}>
            Delete Job
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={onClose}>
          Close
        </Button>
      </div>
    </div>
  );
}
